// Platform-specific icon wrapper
// Uses lucide-react on web, lucide-react-native on native
import { Platform } from 'react-native';
import React from 'react';

// Type for icon components (both libraries have the same interface)
type IconComponent = React.ComponentType<{ size?: number; color?: string; className?: string; style?: any }>;

// Fallback component for missing icons
const FallbackIcon: IconComponent = () => null;

// Conditionally import the icon library based on platform
let iconLib: any = {};

try {
  if (Platform.OS === 'web') {
    // On web, always use lucide-react
    iconLib = require('lucide-react');
  } else {
    // For native platforms, use lucide-react-native
    try {
      // Import all named exports from lucide-react-native
      iconLib = require('lucide-react-native');
    } catch (error) {
      console.error('Error loading lucide-react-native:', error);
      // Fallback to lucide-react if native version fails
      try {
        iconLib = require('lucide-react');
      } catch (fallbackError) {
        console.error('Error loading fallback lucide-react:', fallbackError);
      }
    }
  }
} catch (error) {
  console.error('Error loading icon library:', error);
}

// Re-export all icons from the appropriate library
// This allows us to use the same import path for both web and native
// Use || FallbackIcon to ensure exports are always defined for Metro bundler
export const Mail: IconComponent = iconLib.Mail || FallbackIcon;
export const Lock: IconComponent = iconLib.Lock || FallbackIcon;
export const ArrowRight: IconComponent = iconLib.ArrowRight || FallbackIcon;
export const Loader2: IconComponent = iconLib.Loader2 || FallbackIcon;
export const KeyRound: IconComponent = iconLib.KeyRound || FallbackIcon;
export const Bot: IconComponent = iconLib.Bot || FallbackIcon;
export const Menu: IconComponent = iconLib.Menu || FallbackIcon;
export const X: IconComponent = iconLib.X || FallbackIcon;
export const Home: IconComponent = iconLib.Home || FallbackIcon;
export const User: IconComponent = iconLib.User || FallbackIcon;
export const Briefcase: IconComponent = iconLib.Briefcase || FallbackIcon;
export const Wrench: IconComponent = iconLib.Wrench || FallbackIcon;
export const ClipboardList: IconComponent = iconLib.ClipboardList || FallbackIcon;
export const Calendar: IconComponent = iconLib.Calendar || FallbackIcon;
export const DollarSign: IconComponent = iconLib.DollarSign || FallbackIcon;
export const Settings: IconComponent = iconLib.Settings || FallbackIcon;
export const Package: IconComponent = iconLib.Package || FallbackIcon;
export const Shield: IconComponent = iconLib.Shield || FallbackIcon;
export const Plus: IconComponent = iconLib.Plus || FallbackIcon;
export const Users: IconComponent = iconLib.Users || FallbackIcon;
export const UserPlus: IconComponent = iconLib.UserPlus || FallbackIcon;
export const CheckCircle2: IconComponent = iconLib.CheckCircle2 || FallbackIcon;
export const Layout: IconComponent = iconLib.Layout || FallbackIcon;
export const CalendarClock: IconComponent = iconLib.CalendarClock || FallbackIcon;
export const TrendingUp: IconComponent = iconLib.TrendingUp || FallbackIcon;
export const Receipt: IconComponent = iconLib.Receipt || FallbackIcon;
export const Clock: IconComponent = iconLib.Clock || FallbackIcon;
export const AlertOctagon: IconComponent = iconLib.AlertOctagon || FallbackIcon;
export const MapPin: IconComponent = iconLib.MapPin || FallbackIcon;
export const Trash2: IconComponent = iconLib.Trash2 || FallbackIcon;
export const Upload: IconComponent = iconLib.Upload || FallbackIcon;
export const Image: IconComponent = iconLib.Image || FallbackIcon;
export const Building: IconComponent = iconLib.Building || FallbackIcon;
export const ExternalLink: IconComponent = iconLib.ExternalLink || FallbackIcon;
export const ArrowLeft: IconComponent = iconLib.ArrowLeft || FallbackIcon;
export const AlertTriangle: IconComponent = iconLib.AlertTriangle || FallbackIcon;
export const ChevronDown: IconComponent = iconLib.ChevronDown || FallbackIcon;
export const ChevronUp: IconComponent = iconLib.ChevronUp || FallbackIcon;
export const ChevronRight: IconComponent = iconLib.ChevronRight || FallbackIcon;
export const Send: IconComponent = iconLib.Send || FallbackIcon;
export const Search: IconComponent = iconLib.Search || FallbackIcon;
export const Tag: IconComponent = iconLib.Tag || FallbackIcon;
export const Barcode: IconComponent = iconLib.Barcode || FallbackIcon;
export const Info: IconComponent = iconLib.Info || FallbackIcon;
export const Edit: IconComponent = iconLib.Edit || FallbackIcon;
export const Sparkles: IconComponent = iconLib.Sparkles || FallbackIcon;
export const HelpCircle: IconComponent = iconLib.HelpCircle || FallbackIcon;
export const XCircle: IconComponent = iconLib.XCircle || FallbackIcon;
export const MessageCircle: IconComponent = iconLib.MessageCircle || FallbackIcon;
export const Globe: IconComponent = iconLib.Globe || FallbackIcon;
// Helper function to safely get icon with fallback
const getIcon = (name: string, altName?: string): IconComponent => {
  // Try to get the icon from the library
  let icon = iconLib[name];
  
  // If not found, try alternative name
  if (!icon && altName) {
    icon = iconLib[altName];
  }
  
  // If still not found, try accessing via default export
  if (!icon && iconLib.default) {
    icon = iconLib.default[name] || (altName && iconLib.default[altName]);
  }
  
  // If icon exists, return it (React components can be functions or objects)
  // lucide-react icons are objects (React components), not functions
  if (icon && (typeof icon === 'function' || (typeof icon === 'object' && icon !== null))) {
    return icon;
  }
  
  // Log warning in development only once per icon
  if (__DEV__) {
    const libraryName = Platform.OS === 'web' ? 'lucide-react' : 'lucide-react-native';
    console.warn(`Icon "${name}" not found in ${libraryName}. Using fallback.`);
  }
  
  // Return fallback component
  return FallbackIcon;
};

// Helper function that tries Icon suffix first (for lucide-react v0.553+)
const getIconWithSuffix = (name: string, altName?: string): IconComponent => {
  // Try with Icon suffix first (lucide-react v0.553+ uses this convention)
  const iconNameWithSuffix = `${name}Icon`;
  let icon = iconLib[iconNameWithSuffix];
  
  // Debug logging
  if (__DEV__ && name === 'Eye') {
    console.log('Debug Eye icon lookup:', {
      iconLibType: typeof iconLib,
      hasEyeIcon: !!iconLib[iconNameWithSuffix],
      hasEye: !!iconLib[name],
      iconLibKeys: Object.keys(iconLib).slice(0, 20),
      iconNameWithSuffix,
      name
    });
  }
  
  // If not found, try without suffix
  if (!icon) {
    icon = iconLib[name];
  }
  
  // If still not found, try alternative name
  if (!icon && altName) {
    icon = iconLib[altName];
  }
  
  // If still not found, try accessing via default export
  if (!icon && iconLib.default) {
    icon = iconLib.default[iconNameWithSuffix] || 
           iconLib.default[name] || 
           (altName && iconLib.default[altName]);
  }
  
  // If icon exists, return it (React components can be functions or objects)
  // lucide-react icons are objects (React components), not functions
  if (icon && (typeof icon === 'function' || (typeof icon === 'object' && icon !== null))) {
    return icon;
  }
  
  // Log warning in development only once per icon
  if (__DEV__) {
    const libraryName = Platform.OS === 'web' ? 'lucide-react' : 'lucide-react-native';
    console.warn(`Icon "${name}" not found in ${libraryName}. Using fallback. Available keys:`, Object.keys(iconLib).filter(k => k.toLowerCase().includes(name.toLowerCase())).slice(0, 10));
  }
  
  // Return fallback component
  return FallbackIcon;
};

// Safely export icons with fallbacks
// Use getIconWithSuffix for icons that need the Icon suffix (lucide-react v0.553+)
export const Paperclip: IconComponent = getIconWithSuffix('Paperclip', 'PaperclipIcon');
export const MessageSquare: IconComponent = getIconWithSuffix('MessageSquare', 'MessageSquareIcon');
export const Bug: IconComponent = getIconWithSuffix('Bug', 'BugIcon');
export const Video: IconComponent = getIconWithSuffix('Video', 'VideoIcon');
export const Eye: IconComponent = getIconWithSuffix('Eye', 'EyeIcon');
export const File: IconComponent = getIconWithSuffix('File', 'FileIcon');
export const Download: IconComponent = getIconWithSuffix('Download', 'DownloadIcon');
export const PenTool: IconComponent = getIconWithSuffix('PenTool', 'PenToolIcon');
export const HardHat: IconComponent = getIconWithSuffix('HardHat', 'HardHatIcon');
export const RefreshCw: IconComponent = getIconWithSuffix('RefreshCw', 'RefreshCwIcon');
export const Filter: IconComponent = getIconWithSuffix('Filter', 'FilterIcon');
export const UserCheck: IconComponent = getIconWithSuffix('UserCheck', 'UserCheckIcon');
export const Edit3: IconComponent = getIconWithSuffix('Edit3', 'EditIcon');
export const FileText: IconComponent = getIconWithSuffix('FileText', 'FileTextIcon');
export const Link2: IconComponent = getIconWithSuffix('Link2', 'LinkIcon');
export const AlertCircle: IconComponent = getIconWithSuffix('AlertCircle', 'AlertCircleIcon');
export const StickyNote: IconComponent = getIconWithSuffix('StickyNote', 'StickyNoteIcon');
export const Check: IconComponent = getIconWithSuffix('Check', 'CheckIcon');
export const Phone: IconComponent = getIconWithSuffix('Phone', 'PhoneIcon');
export const LogOut: IconComponent = getIconWithSuffix('LogOut', 'LogOutIcon');
export const UserCog: IconComponent = getIconWithSuffix('UserCog', 'UserCogIcon');
export const GripVertical: IconComponent = getIconWithSuffix('GripVertical', 'GripVerticalIcon');
export const Gavel: IconComponent = getIconWithSuffix('Gavel', 'GavelIcon');
export const Copy: IconComponent = getIconWithSuffix('Copy', 'CopyIcon');
export const Landmark: IconComponent = getIconWithSuffix('Landmark', 'LandmarkIcon');
export const FileSearch: IconComponent = getIconWithSuffix('FileSearch', 'FileSearchIcon');
export const BarChart: IconComponent = getIconWithSuffix('BarChart', 'BarChartIcon');
export const CalendarCheck: IconComponent = getIconWithSuffix('CalendarCheck', 'CalendarCheckIcon');
export const Play: IconComponent = getIconWithSuffix('Play', 'PlayIcon');
export const Pause: IconComponent = getIconWithSuffix('Pause', 'PauseIcon');
export const RotateCcw: IconComponent = getIconWithSuffix('RotateCcw', 'RotateCcwIcon');
export const Gift: IconComponent = getIconWithSuffix('Gift', 'GiftIcon');
export const LayoutList: IconComponent = getIconWithSuffix('LayoutList', 'LayoutListIcon');
export const List: IconComponent = getIconWithSuffix('List', 'ListIcon');
export const CheckSquare: IconComponent = getIconWithSuffix('CheckSquare', 'CheckSquareIcon');
export const Map: IconComponent = getIconWithSuffix('Map', 'MapIcon');
export const ArrowUpDown: IconComponent = getIconWithSuffix('ArrowUpDown', 'ArrowUpDownIcon');
export const ArrowUp: IconComponent = getIconWithSuffix('ArrowUp', 'ArrowUpIcon');
export const ArrowDown: IconComponent = getIconWithSuffix('ArrowDown', 'ArrowDownIcon');
export const Edit2: IconComponent = getIconWithSuffix('Edit2', 'Edit2Icon');
export const Ruler: IconComponent = getIconWithSuffix('Ruler', 'RulerIcon');
export const ClipboardCheck: IconComponent = getIconWithSuffix('ClipboardCheck', 'ClipboardCheckIcon');
export const Bell: IconComponent = getIconWithSuffix('Bell', 'BellIcon');
export const HeartHandshake: IconComponent = getIconWithSuffix('HeartHandshake', 'HeartHandshakeIcon');
export const Building2: IconComponent = getIconWithSuffix('Building2', 'Building2Icon');
export const Star: IconComponent = getIconWithSuffix('Star', 'StarIcon');
export const Award: IconComponent = getIconWithSuffix('Award', 'AwardIcon');
export const SlidersHorizontal: IconComponent = getIconWithSuffix('SlidersHorizontal', 'SlidersHorizontalIcon');
export const Save: IconComponent = getIconWithSuffix('Save', 'SaveIcon');
export const MapPinned: IconComponent = getIconWithSuffix('MapPinned', 'MapPinnedIcon');
export const ChevronLeft: IconComponent = getIconWithSuffix('ChevronLeft', 'ChevronLeftIcon');
export const Repeat: IconComponent = getIconWithSuffix('Repeat', 'RepeatIcon');
export const Camera: IconComponent = getIconWithSuffix('Camera', 'CameraIcon');
export const SkipForward: IconComponent = getIconWithSuffix('SkipForward', 'SkipForwardIcon');

// Export all other icons dynamically
// For any icon not listed above, import directly from this module
export default iconLib;
